import { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Globe, Smartphone, Cpu, Cloud, Shield, BarChart3, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

const services = [
  {
    icon: Globe, title: 'Web Development',
    subtitle: 'Modern web experiences that convert',
    description: 'We build scalable, high-performance web applications using cutting-edge technologies. From sleek marketing websites to complex SaaS platforms, we deliver solutions that engage users and drive growth.',
    features: ['Custom React & Next.js Applications', 'Progressive Web Apps (PWA)', 'E-Commerce Platforms', 'Content Management Systems', 'API Development & Integration', 'Performance Optimization'],
    image: 'https://images.pexels.com/photos/34803987/pexels-photo-34803987.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    color: 'blue',
  },
  {
    icon: Smartphone, title: 'Mobile App Development',
    subtitle: 'Apps people love to use',
    description: 'Native and cross-platform mobile applications engineered for performance and user delight. We take your idea from concept to App Store/Play Store launch and beyond.',
    features: ['iOS & Android Native Development', 'React Native & Flutter Cross-Platform', 'UI/UX Design & Prototyping', 'Push Notifications & Real-time Features', 'App Store Optimization', 'Ongoing Maintenance & Updates'],
    image: 'https://images.pexels.com/photos/27487608/pexels-photo-27487608.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    color: 'cyan',
  },
  {
    icon: Cpu, title: 'Custom Software',
    subtitle: 'Tailored solutions for unique challenges',
    description: 'Enterprise-grade software built from the ground up to solve your specific business problems. We architect, develop, and deploy systems that streamline operations and unlock new capabilities.',
    features: ['Enterprise Resource Planning (ERP)', 'Workflow Automation Tools', 'Data Analytics Platforms', 'Third-Party API Integrations', 'Legacy System Modernization', 'Microservices Architecture'],
    image: 'https://images.pexels.com/photos/28428592/pexels-photo-28428592.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    color: 'indigo',
  },
  {
    icon: Cloud, title: 'Cloud Solutions',
    subtitle: 'Scalable infrastructure for modern apps',
    description: 'We architect and deploy cloud-native solutions on AWS, GCP, and Azure. From serverless functions to Kubernetes clusters, we ensure your apps are reliable, scalable, and cost-efficient.',
    features: ['Cloud Migration & Strategy', 'Serverless Architecture', 'Container Orchestration (Docker & K8s)', 'CI/CD Pipeline Setup', 'Infrastructure as Code', 'Cost Optimization'],
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&auto=format&fit=crop&q=80',
    color: 'teal',
  },
  {
    icon: Shield, title: 'Cybersecurity',
    subtitle: 'Protect your digital assets',
    description: 'Comprehensive security audits, penetration testing, and compliance consulting. We identify vulnerabilities and implement robust defenses to protect your applications and data.',
    features: ['Security Audits & Penetration Testing', 'OWASP Compliance', 'Data Encryption & Privacy', 'Identity & Access Management', 'Incident Response Planning', 'Regulatory Compliance (GDPR, HIPAA)'],
    image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&auto=format&fit=crop&q=80',
    color: 'red',
  },
  {
    icon: BarChart3, title: 'IT Consulting',
    subtitle: 'Strategic guidance for digital transformation',
    description: 'Expert technical consulting to help you navigate the complex technology landscape. We provide actionable insights and strategic roadmaps aligned with your business goals.',
    features: ['Technology Stack Assessment', 'Digital Transformation Roadmap', 'Technical Due Diligence', 'Team Augmentation', 'Architecture Review', 'Process Optimization'],
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop&q=80',
    color: 'amber',
  },
];

const colorMap = {
  blue: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'hover:border-blue-500/30', glow: 'group-hover:shadow-[0_0_40px_-10px_rgba(59,130,246,0.2)]' },
  cyan: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', border: 'hover:border-cyan-500/30', glow: 'group-hover:shadow-[0_0_40px_-10px_rgba(6,182,212,0.2)]' },
  indigo: { bg: 'bg-indigo-500/10', text: 'text-indigo-400', border: 'hover:border-indigo-500/30', glow: 'group-hover:shadow-[0_0_40px_-10px_rgba(99,102,241,0.2)]' },
  teal: { bg: 'bg-teal-500/10', text: 'text-teal-400', border: 'hover:border-teal-500/30', glow: 'group-hover:shadow-[0_0_40px_-10px_rgba(20,184,166,0.2)]' },
  red: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'hover:border-red-500/30', glow: 'group-hover:shadow-[0_0_40px_-10px_rgba(239,68,68,0.2)]' },
  amber: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'hover:border-amber-500/30', glow: 'group-hover:shadow-[0_0_40px_-10px_rgba(245,158,11,0.2)]' },
};

const process_steps = [
  { step: '01', title: 'Discovery', desc: 'Deep-dive into your requirements, goals, and market' },
  { step: '02', title: 'Design', desc: 'Wireframes, prototypes, and visual design iterations' },
  { step: '03', title: 'Develop', desc: 'Agile sprints with continuous feedback and testing' },
  { step: '04', title: 'Deploy', desc: 'Launch, monitoring, and ongoing optimization' },
];

export default function ServicesPage() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="services-page"
    >
      {/* Hero */}
      <section ref={heroRef} className="relative min-h-[60vh] flex items-center overflow-hidden pt-20">
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <img src="https://images.pexels.com/photos/34803987/pexels-photo-34803987.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Services" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/85 to-slate-950" />
        </motion.div>
        <motion.div style={{ opacity: heroOpacity }} className="relative max-w-7xl mx-auto px-6 md:px-12 py-20">
          <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-sm font-medium text-blue-400 tracking-widest uppercase mb-4 block">
            Our Services
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }} className="font-heading font-extrabold text-5xl md:text-7xl tracking-tight text-white mb-6">
            What We <span className="glow-text">Build</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="text-base md:text-lg text-slate-400 max-w-2xl leading-relaxed">
            Comprehensive digital solutions engineered to transform businesses. From idea to deployment, we bring technical excellence to every project.
          </motion.p>
        </motion.div>
      </section>

      {/* Services Grid */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-slate-950" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-20">
            {services.map((service, index) => {
              const c = colorMap[service.color];
              return (
                <ServiceRow key={service.title} service={service} colors={c} index={index} />
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[#0a0f1e]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
        <ProcessSection />
      </section>

      {/* CTA */}
      <section className="relative py-24 bg-slate-950">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
        <motion.div
          animate={{ scale: [1, 1.1, 1], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[150px]"
        />
        <div className="relative max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight text-white mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-base text-slate-400 mb-8 leading-relaxed">
            Let's discuss how we can bring your vision to life with the right technology stack and strategy.
          </p>
          <Button
            data-testid="services-page-cta"
            onClick={() => navigate('/contact')}
            className="bg-blue-600 hover:bg-blue-500 text-white rounded-full px-8 h-12 text-base font-semibold transition-all duration-300 hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] group"
          >
            Get a Free Consultation
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>
    </motion.div>
  );
}

function ServiceRow({ service, colors, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const isEven = index % 2 === 0;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7 }}
      className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${isEven ? '' : 'lg:[direction:rtl]'}`}
    >
      <motion.div
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.4 }}
        className="relative rounded-2xl overflow-hidden aspect-[16/10] lg:[direction:ltr]"
      >
        <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 to-transparent" />
        <div className={`absolute top-5 left-5 p-3 rounded-xl ${colors.bg}`}>
          <service.icon className={`w-6 h-6 ${colors.text}`} />
        </div>
      </motion.div>
      <div className="lg:[direction:ltr]">
        <span className={`text-xs font-semibold tracking-widest uppercase ${colors.text} mb-2 block`}>{service.subtitle}</span>
        <h3 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">{service.title}</h3>
        <p className="text-base text-slate-400 leading-relaxed mb-6">{service.description}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {service.features.map((f, i) => (
            <motion.div
              key={f}
              initial={{ opacity: 0, x: 15 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.3 + i * 0.07 }}
              className="flex items-center gap-2.5"
            >
              <CheckCircle className={`w-4 h-4 ${colors.text} flex-shrink-0`} />
              <span className="text-sm text-slate-300">{f}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

function ProcessSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <div ref={ref} className="relative max-w-7xl mx-auto px-6 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
        className="text-center mb-16"
      >
        <span className="text-sm font-medium text-blue-400 tracking-widest uppercase mb-4 block">How We Work</span>
        <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-white">Our Process</h2>
      </motion.div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {process_steps.map((item, i) => (
          <motion.div
            key={item.step}
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 + i * 0.15, duration: 0.6 }}
            whileHover={{ y: -6 }}
            className="relative p-6 rounded-2xl border border-white/5 bg-slate-900/30 text-center group hover:border-blue-500/20 transition-all duration-300"
          >
            <div className="text-4xl font-heading font-extrabold text-blue-500/20 mb-3 group-hover:text-blue-500/40 transition-colors">{item.step}</div>
            <h4 className="font-heading font-semibold text-lg text-white mb-2">{item.title}</h4>
            <p className="text-sm text-slate-400">{item.desc}</p>
            {i < 3 && <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-px bg-gradient-to-r from-blue-500/30 to-transparent" />}
          </motion.div>
        ))}
      </div>
    </div>
  );
}
